import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./context/ThemeProvider";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { Toaster } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ProfilePage from "./pages/ProfilePage";
import DashboardPage from "./pages/DashboardPage";
import CallAnalysisPage from "./pages/CallAnalysisPage";
import PublisherReportPage from "./pages/PublisherReportPage";
import BuyerOptimizerPage from "./pages/BuyerOptimizerPage";
import ActionCenterPage from "./pages/ActionCenterPage";
import SettingsPage from "./pages/SettingsPage";
import PublisherActivityPage from "./pages/PublisherActivityPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import PublisherRankingPage from "./pages/PublisherRankingPage";
import PublisherExclusionsPage from "./pages/PublisherExclusionsPage";
import UserManagementPage from "./pages/UserManagementPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import UserDetailPage from "./pages/UserDetailPage";
import AuditLogPage from "./pages/AuditLogPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import BuyCreditsPage from "./pages/BuyCreditsPage";
import PaymentManagementPage from "./pages/PaymentManagementPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import TermsPage from "./pages/TermsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import EmergentCreditsPage from "./pages/EmergentCreditsPage";
import DIDStatusPage from "./pages/DIDStatusPage";
import SoftwareGuidePage from "./pages/SoftwareGuidePage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Admin-only Protected Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      {/* Signup route hidden — uncomment to re-enable */}
      {/* <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      /> */}
      <Route
        path="/verify-email"
        element={<VerifyEmailPage />}
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/terms"
        element={<TermsPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/call-analysis"
        element={
          <ProtectedRoute>
            <CallAnalysisPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publisher-report"
        element={
          <ProtectedRoute>
            <PublisherReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyer-optimizer"
        element={
          <ProtectedRoute>
            <BuyerOptimizerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-center"
        element={
          <ProtectedRoute>
            <ActionCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <AdminRoute>
            <SettingsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/publisher-activity"
        element={
          <AdminRoute>
            <PublisherActivityPage />
          </AdminRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publisher-ranking"
        element={
          <ProtectedRoute>
            <PublisherRankingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/publisher-exclusions"
        element={
          <AdminRoute>
            <PublisherExclusionsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <AdminRoute>
            <UserManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path="/user-management/:userId"
        element={
          <AdminRoute>
            <UserDetailPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin-overview"
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <AdminRoute>
            <AuditLogPage />
          </AdminRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <AdminRoute>
            <AnnouncementsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/buy-credits"
        element={
          <ProtectedRoute>
            <BuyCreditsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-management"
        element={
          <AdminRoute>
            <PaymentManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-log"
        element={
          <ProtectedRoute>
            <ActivityLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergent-credits"
        element={
          <ProtectedRoute>
            <EmergentCreditsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/did-status"
        element={
          <ProtectedRoute>
            <DIDStatusPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/software-guide"
        element={
          <AdminRoute>
            <SoftwareGuidePage />
          </AdminRoute>
        }
      />
      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="digital-master-theme">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                color: "hsl(var(--foreground))",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
